import { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import { RouteErrorBoundary } from './components/ErrorBoundary';
import { PortalAuthProvider, usePortalAuth } from './hooks/usePortalAuth';

// Eager load critical pages
import LandingPage from './pages/LandingPage';
import Login from './pages/Login';
import Layout from './components/Layout';

// Customer Portal pages
const PortalLoginPage = lazy(() => import('./pages/portal/PortalLoginPage'));
const PortalLayout = lazy(() => import('./pages/portal/PortalLayout'));
const PortalDashboardPage = lazy(() => import('./pages/portal/PortalDashboardPage'));
const PortalOrdersPage = lazy(() => import('./pages/portal/PortalOrdersPage'));
const PortalOrderDetailPage = lazy(() => import('./pages/portal/PortalOrderDetailPage'));
const PortalDocumentsPage = lazy(() => import('./pages/portal/PortalDocumentsPage'));
const PortalBatchCoaPage = lazy(() => import('./pages/portal/PortalBatchCoaPage'));
const PortalSamplesPage = lazy(() => import('./pages/portal/PortalSamplesPage'));
const PortalInvoicesPage = lazy(() => import('./pages/portal/PortalInvoicesPage'));
const PortalSupportPage = lazy(() => import('./pages/portal/PortalSupportPage'));

// Lazy load app pages for better code splitting
const Dashboard = lazy(() => import('./pages/Dashboard'));
const FormulasPage = lazy(() => import('./pages/formulas/FormulasPage'));
const FormulaNewPage = lazy(() => import('./pages/formulas/FormulaNewPage'));
const FormulaEditPage = lazy(() => import('./pages/formulas/FormulaEditPage'));
const FormulaDetailPage = lazy(() => import('./pages/formulas/FormulaDetailPage'));
const FormulaComparePage = lazy(() => import('./pages/formulas/FormulaComparePage'));
const ProductDesignerPage = lazy(() => import('./pages/formulas/ProductDesignerPage'));
const RawMaterialsPage = lazy(() => import('./pages/raw-materials/RawMaterialsPage'));
const RawMaterialNewPage = lazy(() => import('./pages/raw-materials/RawMaterialNewPage'));
const RawMaterialDetailPage = lazy(() => import('./pages/raw-materials/RawMaterialDetailPage'));
const RawMaterialEditPage = lazy(() => import('./pages/raw-materials/RawMaterialEditPage'));
const InventoryPage = lazy(() => import('./pages/inventory/InventoryPage'));
const LotReceivePage = lazy(() => import('./pages/inventory/LotReceivePage'));
const LotDetailPage = lazy(() => import('./pages/inventory/LotDetailPage'));
const WarehousesPage = lazy(() => import('./pages/inventory/WarehousesPage'));
const ProductionPage = lazy(() => import('./pages/production/ProductionPage'));
const ProductionOrderNewPage = lazy(() => import('./pages/production/ProductionOrderNewPage'));
const ProductionOrderDetailPage = lazy(() => import('./pages/production/ProductionOrderDetailPage'));
const BatchesPage = lazy(() => import('./pages/production/BatchesPage'));
const BatchDetailPage = lazy(() => import('./pages/production/BatchDetailPage'));
const CustomersPage = lazy(() => import('./pages/sales/CustomersPage'));
const CustomerNewPage = lazy(() => import('./pages/sales/CustomerNewPage'));
const CustomerDetailPage = lazy(() => import('./pages/sales/CustomerDetailPage'));
const CustomerEditPage = lazy(() => import('./pages/sales/CustomerEditPage'));
const QuotesPage = lazy(() => import('./pages/sales/QuotesPage'));
const QuoteNewPage = lazy(() => import('./pages/sales/QuoteNewPage'));
const QuoteDetailPage = lazy(() => import('./pages/sales/QuoteDetailPage'));
const OrdersPage = lazy(() => import('./pages/sales/OrdersPage'));
const OrderNewPage = lazy(() => import('./pages/sales/OrderNewPage'));
const OrderDetailPage = lazy(() => import('./pages/sales/OrderDetailPage'));
const SettingsPage = lazy(() => import('./pages/settings/SettingsPage'));
const UsersPage = lazy(() => import('./pages/users/UsersPage'));
// ReportsPage consolidated into AnalyticsPage
const SuppliersPage = lazy(() => import('./pages/suppliers/SuppliersPage'));
const SupplierNewPage = lazy(() => import('./pages/suppliers/SupplierNewPage'));
const SupplierDetailPage = lazy(() => import('./pages/suppliers/SupplierDetailPage'));
const SupplierEditPage = lazy(() => import('./pages/suppliers/SupplierEditPage'));
const SupplierPerformancePage = lazy(() => import('./pages/suppliers/SupplierPerformancePage'));
const PurchaseOrdersPage = lazy(() => import('./pages/purchasing/PurchaseOrdersPage'));
const ProcurementPage = lazy(() => import('./pages/procurement/ProcurementPage'));
const RfqsPage = lazy(() => import('./pages/purchasing/RfqsPage'));
const RfqNewPage = lazy(() => import('./pages/purchasing/RfqNewPage'));
const RfqDetailPage = lazy(() => import('./pages/purchasing/RfqDetailPage'));
const PurchaseOrderNewPage = lazy(() => import('./pages/purchasing/PurchaseOrderNewPage'));
const PurchaseOrderDetailPage = lazy(() => import('./pages/purchasing/PurchaseOrderDetailPage'));
const ActivityLogPage = lazy(() => import('./pages/activity/ActivityLogPage'));
const UserActivityPage = lazy(() => import('./pages/activity/UserActivityPage'));
const TraceabilityPage = lazy(() => import('./pages/traceability/TraceabilityPage'));
const QualityControlPage = lazy(() => import('./pages/quality/QualityControlPage'));
const CostCalculatorPage = lazy(() => import('./pages/costing/CostCalculatorPage'));
const ExchangeRatesPage = lazy(() => import('./pages/costing/ExchangeRatesPage'));
const SupplierMaterialsPage = lazy(() => import('./pages/costing/SupplierMaterialsPage'));
const AlertsPage = lazy(() => import('./pages/alerts/AlertsPage'));
const EmailInboxPage = lazy(() => import('./pages/emails/EmailInboxPage'));
const SpecificationsPage = lazy(() => import('./pages/quality/SpecificationsPage'));
const EquipmentPage = lazy(() => import('./pages/equipment/EquipmentPage'));
const PlanningPage = lazy(() => import('./pages/planning/PlanningPage'));
const WastePage = lazy(() => import('./pages/waste/WastePage'));
const DocumentsPage = lazy(() => import('./pages/documents/DocumentsPage'));
const NotificationPreferencesPage = lazy(() => import('./pages/notifications/NotificationPreferencesPage'));
const YieldAnalysisPage = lazy(() => import('./pages/yield-analysis/YieldAnalysisPage'));
const BatchCostingPage = lazy(() => import('./pages/costing/BatchCostingPage'));
const SamplesPage = lazy(() => import('./pages/samples/SamplesPage'));
const PriceListsPage = lazy(() => import('./pages/pricing/PriceListsPage'));
const ContractsPage = lazy(() => import('./pages/contracts/ContractsPage'));
const CertificationsPage = lazy(() => import('./pages/certifications/CertificationsPage'));
const ShippingPage = lazy(() => import('./pages/shipping/ShippingPage'));
const ShipmentDetailPage = lazy(() => import('./pages/shipping/ShipmentDetailPage'));
const SDSPage = lazy(() => import('./pages/sds/SDSPage'));
const StabilityStudiesPage = lazy(() => import('./pages/stability/StabilityStudiesPage'));
const RegulatoryPage = lazy(() => import('./pages/regulatory/RegulatoryPage'));
const SPCPage = lazy(() => import('./pages/spc/SPCPage'));
const ESignaturesPage = lazy(() => import('./pages/esignatures/ESignaturesPage'));
// ForecastingPage consolidated into AnalyticsPage
const SustainabilityPage = lazy(() => import('./pages/sustainability/SustainabilityPage'));
const InvoicesPage = lazy(() => import('./pages/invoices/InvoicesPage'));
const TransfersPage = lazy(() => import('./pages/transfers/TransfersPage'));
const SupplyChainRisksPage = lazy(() => import('./pages/supply-chain-risks/SupplyChainRisksPage'));
const BatchLabelPage = lazy(() => import('./pages/production/BatchLabelPage'));
const ReorderPointsPage = lazy(() => import('./pages/reorder-points/ReorderPointsPage'));
const LabPage = lazy(() => import('./pages/lab/LabPage'));
const MRPPage = lazy(() => import('./pages/mrp/MRPPage'));
const PaymentsPage = lazy(() => import('./pages/payments/PaymentsPage'));
const SensoryPage = lazy(() => import('./pages/sensory/SensoryPage'));
const AllergenManagementPage = lazy(() => import('./pages/allergens/AllergenManagementPage'));
const MaintenancePage = lazy(() => import('./pages/maintenance/MaintenancePage'));
const TrainingPage = lazy(() => import('./pages/training/TrainingPage'));
const SchedulingPage = lazy(() => import('./pages/scheduling/SchedulingPage'));
const ProjectsPage = lazy(() => import('./pages/projects/ProjectsPage'));
const EHSPage = lazy(() => import('./pages/ehs/EHSPage'));
const CalibrationPage = lazy(() => import('./pages/calibration/CalibrationPage'));
const CustomerFeedbackPage = lazy(() => import('./pages/customer-feedback/CustomerFeedbackPage'));
const EBRPage = lazy(() => import('./pages/ebr/EBRPage'));
const AnalyticsPage = lazy(() => import('./pages/analytics/AnalyticsPage'));
const AuditPage = lazy(() => import('./pages/audit/AuditPage'));
const PackagingPage = lazy(() => import('./pages/packaging/PackagingPage'));
const CompliancePage = lazy(() => import('./pages/compliance/CompliancePage'));
const IntegrationsPage = lazy(() => import('./pages/integrations/IntegrationsPage'));
const VendorQualificationPage = lazy(() => import('./pages/vendor-qualification/VendorQualificationPage'));
const FinancialAnalyticsPage = lazy(() => import('./pages/financial-analytics/FinancialAnalyticsPage'));
const CRMPage = lazy(() => import('./pages/crm/CRMPage'));
const ContractManufacturingPage = lazy(() => import('./pages/contract-manufacturing/ContractManufacturingPage'));
const IPManagementPage = lazy(() => import('./pages/ip-management/IPManagementPage'));
const WorkflowsPage = lazy(() => import('./pages/workflows/WorkflowsPage'));
const LocationsPage = lazy(() => import('./pages/locations/LocationsPage'));
const GenealogyPage = lazy(() => import('./pages/genealogy/GenealogyPage'));
const WebhooksPage = lazy(() => import('./pages/webhooks/WebhooksPage'));
const ApiKeysPage = lazy(() => import('./pages/api-keys/ApiKeysPage'));
const RecipeDevelopmentPage = lazy(() => import('./pages/recipe-development/RecipeDevelopmentPage'));
const PricingEnginePage = lazy(() => import('./pages/pricing-engine/PricingEnginePage'));
const ProductBriefsPage = lazy(() => import('./pages/product-briefs/ProductBriefsPage'));
const ReportTemplatesPage = lazy(() => import('./pages/report-templates/ReportTemplatesPage'));
const AIInsightsPage = lazy(() => import('./pages/ai/AIInsightsPage'));
const GCMSPage = lazy(() => import('./pages/gcms/GCMSPage'));
const ValidationPage = lazy(() => import('./pages/validation/ValidationPage'));
// BusinessIntelligencePage consolidated into AnalyticsPage
const CostOptimizationPage = lazy(() => import('./pages/cost-optimization/CostOptimizationPage'));
const OrderManagementPage = lazy(() => import('./pages/oms/OrderManagementPage'));
const ShopifyManagementPage = lazy(() => import('./pages/ecommerce/ShopifyManagementPage'));
const WarehouseManagementPage = lazy(() => import('./pages/inventory/WarehouseManagementPage'));
const CrmManagementPage = lazy(() => import('./pages/crm/CrmManagementPage'));
const BatchRecordsPage = lazy(() => import('./pages/batch-records/BatchRecordsPage'));
const CapaManagementPage = lazy(() => import('./pages/capa/CapaManagementPage'));
const BiDashboardPage = lazy(() => import('./pages/bi/BiDashboardPage'));
const AdvancedInventoryPage = lazy(() => import('./pages/inventory/AdvancedInventoryPage'));
const AuditManagementPage = lazy(() => import('./pages/audit/AuditManagementPage'));
const ShelfLifePage = lazy(() => import('./pages/shelf-life/ShelfLifePage'));
const DeviationsPage = lazy(() => import('./pages/deviations/DeviationsPage'));
const NCRPage = lazy(() => import('./pages/ncr/NCRPage'));
const OOSPage = lazy(() => import('./pages/oos/OOSPage'));
const ChangeControlPage = lazy(() => import('./pages/change-control/ChangeControlPage'));
const ComplaintsCapaPage = lazy(() => import('./pages/complaints-capa/ComplaintsCapaPage'));

// Page loading fallback
function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-[400px]" role="status" aria-label="Loading page">
      <div className="flex flex-col items-center gap-3">
        <div className="w-8 h-8 rounded-full border-3 border-gray-200 border-t-gray-900 animate-spin" aria-hidden="true" />
        <p className="text-gray-500 text-sm">Loading...</p>
      </div>
    </div>
  );
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-cream" role="status" aria-label="Authenticating">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 rounded-full border-4 border-gold/20 border-t-gold animate-spin" aria-hidden="true" />
          <p className="text-charcoal/50 text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    if (import.meta.env.DEV) {
      return <Navigate to="/login" replace />;
    }
    window.location.href = '/login';
    return null;
  }

  return <>{children}</>;
}

function ProtectedPortalRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = usePortalAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50" role="status" aria-label="Authenticating">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 rounded-full border-4 border-amber-200 border-t-amber-500 animate-spin" aria-hidden="true" />
          <p className="text-gray-500 text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/portal/login" replace />;
  }

  return <>{children}</>;
}

export default function App() {
  return (
    <Routes>
      {/* Public Landing Page */}
      <Route path="/" element={<LandingPage />} />

      {/* Login route */}
      <Route path="/login" element={<Login />} />

      {/* Customer Portal routes */}
      <Route
        path="/portal/*"
        element={
          <PortalAuthProvider>
            <RouteErrorBoundary>
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  <Route path="/login" element={<PortalLoginPage />} />
                  <Route
                    path="/*"
                    element={
                      <ProtectedPortalRoute>
                        <PortalLayout>
                          <Routes>
                            <Route path="/" element={<PortalDashboardPage />} />
                            <Route path="/orders" element={<PortalOrdersPage />} />
                            <Route path="/orders/:id" element={<PortalOrderDetailPage />} />
                            <Route path="/documents" element={<PortalDocumentsPage />} />
                            <Route path="/samples" element={<PortalSamplesPage />} />
                            <Route path="/invoices" element={<PortalInvoicesPage />} />
                            <Route path="/support" element={<PortalSupportPage />} />
                            <Route path="/batches/:id/coa" element={<PortalBatchCoaPage />} />
                          </Routes>
                        </PortalLayout>
                      </ProtectedPortalRoute>
                    }
                  />
                </Routes>
              </Suspense>
            </RouteErrorBoundary>
          </PortalAuthProvider>
        }
      />

      {/* Protected App routes */}
      <Route
        path="/app/*"
        element={
          <ProtectedRoute>
            <Layout>
              <RouteErrorBoundary>
                <Suspense fallback={<PageLoader />}>
                  <Routes>
                    <Route path="/" element={<Dashboard />} />
                    <Route path="/formulas" element={<FormulasPage />} />
                    <Route path="/formulas/new" element={<FormulaNewPage />} />
                    <Route path="/formulas/:id" element={<FormulaDetailPage />} />
                    <Route path="/formulas/:id/edit" element={<FormulaEditPage />} />
                    <Route path="/formulas/:id/designer" element={<ProductDesignerPage />} />
                    <Route path="/formulas/compare" element={<FormulaComparePage />} />
                    <Route path="/raw-materials" element={<RawMaterialsPage />} />
                    <Route path="/raw-materials/new" element={<RawMaterialNewPage />} />
                    <Route path="/raw-materials/:id" element={<RawMaterialDetailPage />} />
                    <Route path="/raw-materials/:id/edit" element={<RawMaterialEditPage />} />
                    <Route path="/inventory" element={<InventoryPage />} />
                    <Route path="/inventory/receive" element={<LotReceivePage />} />
                    <Route path="/inventory/lots/:id" element={<LotDetailPage />} />
                    <Route path="/warehouses" element={<WarehousesPage />} />
                    <Route path="/production" element={<ProductionPage />} />
                    <Route path="/production/new" element={<ProductionOrderNewPage />} />
                    <Route path="/production/:id" element={<ProductionOrderDetailPage />} />
                    <Route path="/batches" element={<BatchesPage />} />
                    <Route path="/batches/:id" element={<BatchDetailPage />} />
                    <Route path="/batches/:id/label" element={<BatchLabelPage />} />
                    <Route path="/customers" element={<CustomersPage />} />
                    <Route path="/customers/new" element={<CustomerNewPage />} />
                    <Route path="/customers/:id" element={<CustomerDetailPage />} />
                    <Route path="/customers/:id/edit" element={<CustomerEditPage />} />
                    <Route path="/quotes" element={<QuotesPage />} />
                    <Route path="/quotes/new" element={<QuoteNewPage />} />
                    <Route path="/quotes/:id" element={<QuoteDetailPage />} />
                    <Route path="/orders" element={<OrdersPage />} />
                    <Route path="/orders/new" element={<OrderNewPage />} />
                    <Route path="/orders/:id" element={<OrderDetailPage />} />
                    <Route path="/settings" element={<SettingsPage />} />
                    <Route path="/users" element={<UsersPage />} />
                    <Route path="/reports" element={<AnalyticsPage />} />
                    <Route path="/suppliers" element={<SuppliersPage />} />
                    <Route path="/suppliers/new" element={<SupplierNewPage />} />
                    <Route path="/suppliers/:id" element={<SupplierDetailPage />} />
                    <Route path="/suppliers/:id/edit" element={<SupplierEditPage />} />
                    <Route path="/supplier-performance" element={<SupplierPerformancePage />} />
                    <Route path="/purchasing" element={<PurchaseOrdersPage />} />
                    <Route path="/purchasing/new" element={<PurchaseOrderNewPage />} />
                    <Route path="/purchasing/:id" element={<PurchaseOrderDetailPage />} />
                    <Route path="/procurement" element={<ProcurementPage />} />
                    <Route path="/order-management" element={<OrderManagementPage />} />
                    <Route path="/shopify" element={<ShopifyManagementPage />} />
                    <Route path="/warehouse-management" element={<WarehouseManagementPage />} />
                    <Route path="/crm-management" element={<CrmManagementPage />} />
                    <Route path="/batch-records" element={<BatchRecordsPage />} />
                    <Route path="/capa" element={<CapaManagementPage />} />
                    <Route path="/bi" element={<BiDashboardPage />} />
                    <Route path="/advanced-inventory" element={<AdvancedInventoryPage />} />
                    <Route path="/audit-management" element={<AuditManagementPage />} />
                    <Route path="/rfqs" element={<RfqsPage />} />
                    <Route path="/rfqs/new" element={<RfqNewPage />} />
                    <Route path="/rfqs/:id" element={<RfqDetailPage />} />
                    <Route path="/activity" element={<ActivityLogPage />} />
                    <Route path="/user-activity" element={<UserActivityPage />} />
                    <Route path="/traceability" element={<TraceabilityPage />} />
                    <Route path="/quality" element={<QualityControlPage />} />
                    <Route path="/costing" element={<CostCalculatorPage />} />
                    <Route path="/costing/exchange-rates" element={<ExchangeRatesPage />} />
                    <Route path="/costing/supplier-materials" element={<SupplierMaterialsPage />} />
                    <Route path="/alerts" element={<AlertsPage />} />
                    <Route path="/emails" element={<EmailInboxPage />} />
                    <Route path="/specifications" element={<SpecificationsPage />} />
                    <Route path="/equipment" element={<EquipmentPage />} />
                    <Route path="/planning" element={<PlanningPage />} />
                    <Route path="/waste" element={<WastePage />} />
                    <Route path="/documents" element={<DocumentsPage />} />
                    <Route path="/notification-settings" element={<NotificationPreferencesPage />} />
                    <Route path="/yield-analysis" element={<YieldAnalysisPage />} />
                    <Route path="/batch-costing" element={<BatchCostingPage />} />
                    <Route path="/samples" element={<SamplesPage />} />
                    <Route path="/price-lists" element={<PriceListsPage />} />
                    <Route path="/contracts" element={<ContractsPage />} />
                    <Route path="/certifications" element={<CertificationsPage />} />
                    <Route path="/shipping" element={<ShippingPage />} />
                    <Route path="/shipping/:id" element={<ShipmentDetailPage />} />
                    <Route path="/sds" element={<SDSPage />} />
                    <Route path="/stability" element={<StabilityStudiesPage />} />
                    <Route path="/regulatory" element={<RegulatoryPage />} />
                    <Route path="/spc" element={<SPCPage />} />
                    <Route path="/esignatures" element={<ESignaturesPage />} />
                    <Route path="/forecasting" element={<AnalyticsPage />} />
                    <Route path="/sustainability" element={<SustainabilityPage />} />
                    <Route path="/invoices" element={<InvoicesPage />} />
                    <Route path="/transfers" element={<TransfersPage />} />
                    <Route path="/supply-chain-risks" element={<SupplyChainRisksPage />} />
                    <Route path="/reorder-points" element={<ReorderPointsPage />} />
                    <Route path="/lab" element={<LabPage />} />
                    <Route path="/mrp" element={<MRPPage />} />
                    <Route path="/payments" element={<PaymentsPage />} />
                    <Route path="/sensory" element={<SensoryPage />} />
                    <Route path="/allergens" element={<AllergenManagementPage />} />
                    <Route path="/maintenance" element={<MaintenancePage />} />
                    <Route path="/training" element={<TrainingPage />} />
                    <Route path="/scheduling" element={<SchedulingPage />} />
                    <Route path="/projects" element={<ProjectsPage />} />
                    <Route path="/ehs" element={<EHSPage />} />
                    <Route path="/calibration" element={<CalibrationPage />} />
                    <Route path="/customer-feedback" element={<CustomerFeedbackPage />} />
                    <Route path="/ebr" element={<EBRPage />} />
                    <Route path="/analytics" element={<AnalyticsPage />} />
                    <Route path="/audit" element={<AuditPage />} />
                    <Route path="/packaging" element={<PackagingPage />} />
                    <Route path="/compliance" element={<CompliancePage />} />
                    <Route path="/integrations" element={<IntegrationsPage />} />
                    <Route path="/vendor-qualification" element={<VendorQualificationPage />} />
                    <Route path="/financial-analytics" element={<FinancialAnalyticsPage />} />
                    <Route path="/crm" element={<CRMPage />} />
                    <Route path="/contract-manufacturing" element={<ContractManufacturingPage />} />
                    <Route path="/ip-management" element={<IPManagementPage />} />
                    <Route path="/workflows" element={<WorkflowsPage />} />
                    <Route path="/locations" element={<LocationsPage />} />
                    <Route path="/genealogy" element={<GenealogyPage />} />
                    <Route path="/webhooks" element={<WebhooksPage />} />
                    <Route path="/api-keys" element={<ApiKeysPage />} />
                    <Route path="/recipe-development" element={<RecipeDevelopmentPage />} />
                    <Route path="/pricing-engine" element={<PricingEnginePage />} />
                    <Route path="/product-briefs" element={<ProductBriefsPage />} />
                    <Route path="/report-templates" element={<ReportTemplatesPage />} />
                    <Route path="/ai" element={<AIInsightsPage />} />
                    <Route path="/gcms" element={<GCMSPage />} />
                    <Route path="/validation" element={<ValidationPage />} />
                    <Route path="/cost-optimization" element={<CostOptimizationPage />} />
                    <Route path="/shelf-life" element={<ShelfLifePage />} />
                    <Route path="/deviations" element={<DeviationsPage />} />
                    <Route path="/ncr" element={<NCRPage />} />
                    <Route path="/oos" element={<OOSPage />} />
                    <Route path="/change-control" element={<ChangeControlPage />} />
                    <Route path="/complaints-capa" element={<ComplaintsCapaPage />} />
                  </Routes>
                </Suspense>
              </RouteErrorBoundary>
            </Layout>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}
